import { css } from '@emotion/css';
import { Table } from 'antd';
import TileButton, { tileDataProp } from 'components/atoms/TileButton';
import ErrorBox from 'components/molecules/ErrorBox/ErrorBox';
import CopyToClipBoard from 'components/molecules/MicroIntractions/CopyTOClipBoard';
import DownloadExcel from 'components/molecules/MicroIntractions/DownloadExcel';
import { subHeaderClass } from 'components/template/GrowthAnalysis/ExpandedView';
import { UserContext } from 'contexts/ConfigContext';
import { FilterContextData } from 'contexts/FilterContext';
import { useContainerWidth } from 'hooks/general/useContainerWidth';
import { useFetchProfitPool } from 'hooks/growthAnalysis/useFetchProfitPoolChart';
import { useFetchSalesAttribute } from 'hooks/growthAnalysis/useFetchSalesAttribute';
import _ from 'lodash';
import { salesAttributionChartDownload } from 'parser/graphToExcelDownloadParser';
import React, { FC, useContext, useEffect, useMemo, useRef, useState } from 'react';
import tw from 'twin.macro';
import { getSalesAttributionColumns } from 'utils/getSalesAttributionColumns';
import { getAdditionalDataOnCopyClick, getConfig, getKeyByValue } from 'utils/helperMethods';
import { trackEventMixpanel } from 'utils/mixpanel';
import { GROWTH_ANALYSIS_SALES_CHART_FACTOR_CLICKED } from 'utils/mixpanelEventNames';
const buttonList = [
    { key: 'volume_sales', label: 'Volume' },
    { key: 'value_sales', label: 'Value' },
];
export const tableStyle = css`
    .ant-table-thead .ant-table-cell {
        ${tw`bg-greyBackground text-fontColor !font-semibold`}
    }
    .ant-table-thead > tr {
        height: 40px;
    }

    .ant-table-thead
        > tr
        > th:not(:last-child):not(.ant-table-selection-column):not(.ant-table-row-expand-icon-cell):not(
            [colspan]
        ):before {
        background: none;
    }

    .ant-table-tbody > tr.ant-table-row:hover > td {
        background: unset;
    }
`;

const profitPoolRetailerHeader = 'Profit Waterfall Chart: Retailer';
const profitPoolOwnHeader = 'Profit Waterfall Chart: Own';

interface Props {
    onClick?: Function;
    selected?: string;
    showTileButtons?: boolean;
    index: number;
    stringifiedFilter: string;
    volTile: tileDataProp;
    loading: boolean;
    expandedItem: any;
    timePeriod: string | string[];
    comparePeriodEnabled: boolean;
    comparisonPeriodType?: string;
    comparePeriodLabel: string;
}

//SALES ATTRIBUTION CHART COMPONENT
const SalesAttributionChart: FC<Props> = ({
    index,
    onClick,
    selected,
    showTileButtons = false,
    stringifiedFilter,
    volTile,
    loading,
    expandedItem,
    timePeriod,
    comparePeriodEnabled,
    comparisonPeriodType,
    comparePeriodLabel,
}) => {
    const attributionChartHeader = `Sales Attribution Chart vs ${comparePeriodLabel} :`;
    const containerWidth = useContainerWidth({ containerId: 'saleAttributionTable', buffer: 16 * 2 + 6 }); //buffer calculation (16*2 gives the padding and 6 subtracting borders of 1 px)
    const [attributeTile, setAttributeTile] = useState(buttonList[0]);
    const { appConfig } = useContext(UserContext);
    const growthAnalysisConfig = getConfig('growthAnalysis', appConfig);
    const globalFilterConfig = getConfig('globalFilter', appConfig);
    const commonConfig = getConfig('commonConfig', appConfig);
    const { decimalForPercentage } = commonConfig;
    const replacementKeyMapping = globalFilterConfig?.replacementKeyMapping;
    const defaultPeriod = globalFilterConfig?.defaultPeriod;
    const { salesAttributionChart: salesAttributionChartConfig, includeAggPeriod } = growthAnalysisConfig;
    const salesAttributionTableRef = useRef<any>(null);
    const salesAttributionChartRef = useRef<any>();
    const {
        timePeriodSelection,
        allCheckedAttribute,
        timePeriodList,
        screenFilterConfig,
        timeSelectionLabel,
        currentFrequency,
    } = useContext(FilterContextData);

    const {
        data: featureData,
        loading: featureLoading,
        error: featureError,
        extra,
        fetchAttributeSales: fetchFeatureData,
    } = useFetchSalesAttribute();
    const {
        data: featureDataDefault,
        loading: featureDataDefaultLoading,
        error: featureDataDefaultError,
        extra: featureDataDefaultExtra,
        fetchAttributeSales: fetchFeatureDataDefault,
    } = useFetchSalesAttribute();
    const {
        data: profitPoolData,
        loading: profitPoolDataLoading,
        error: profitPoolError,
        fetchProfitPool,
        extra: profitPoolExtra,
    } = useFetchProfitPool();

    //checks if current dropdown is profit
    const isProfit = volTile.key === 'profit';

    //default time period object
    const defaultTimePeriodObject = useMemo(() => {
        const defaultTimePeriod = defaultPeriod?.find(
            (item: string) => item?.[item?.length - 1]?.toLowerCase() === currentFrequency?.key?.toLowerCase(),
        );
        const defaultTimePeriodObject = timePeriodList?.find(
            (item: any) => item?.timePeriod?.toLowerCase() === defaultTimePeriod?.toLowerCase(),
        );

        return {
            timePeriodSelection: {
                start_timestamp: defaultTimePeriodObject?.startTimestamp,
                end_timestamp: defaultTimePeriodObject?.endTimestamp,
            },
            timeSelectionLabel: defaultTimePeriodObject?.timePeriod,
        };
    }, [stringifiedFilter]);

    const commonLoading = featureDataDefaultLoading || featureLoading;
    const commonError = featureDataDefaultError || featureError;

    //common variables used for api call
    const commonVariables = useMemo(() => {
        return {
            contributionType: volTile?.dataKey?.toLowerCase(),
            filter: stringifiedFilter,
            timePeriod,
            frequency: currentFrequency?.key,
            featureName: screenFilterConfig?.dimensionListKey ?? 'default',
            comparisonPeriodType,
            aggPeriod:
                !includeAggPeriod ||
                (comparePeriodEnabled &&
                    comparisonPeriodType !== 'year_ago' &&
                    comparisonPeriodType !== 'prior_period')
                    ? ''
                    : String(timeSelectionLabel)?.toLowerCase() === 'custom date' ||
                      String(timeSelectionLabel)?.toLowerCase() === 'ytd'
                    ? ''
                    : getKeyByValue(replacementKeyMapping, String(timeSelectionLabel)?.toUpperCase()),
        };
    }, [volTile, stringifiedFilter, timePeriod, timeSelectionLabel, screenFilterConfig, loading]);

    //default time period api call
    useEffect(() => {
        if (defaultTimePeriodObject?.timePeriodSelection && defaultTimePeriodObject?.timeSelectionLabel && !loading) {
            const variables = {
                ...commonVariables,
                // timePeriod: JSON.stringify(defaultTimePeriodObject?.timePeriodSelection),
                aggPeriod: !includeAggPeriod
                    ? ''
                    : String(defaultTimePeriodObject?.timeSelectionLabel)?.toLowerCase() === 'custom date'
                    ? ''
                    : getKeyByValue(
                          replacementKeyMapping,
                          String(defaultTimePeriodObject?.timeSelectionLabel)?.toUpperCase(),
                      ),
            };
            fetchFeatureDataDefault({ variables });
        }
    }, [volTile, defaultTimePeriodObject, screenFilterConfig, loading]);

    useEffect(() => {
        const variables = {
            ...commonVariables,
        };
        if (!loading) {
            //for profit, get profit pool data
            if (isProfit) {
                fetchProfitPool({ variables });
            } else {
                fetchFeatureData({ variables });
            }
        }
    }, [volTile, stringifiedFilter, timePeriod, screenFilterConfig, loading]);

    //default and current timeperiod data together
    const commonData = useMemo(() => {
        if (featureData?.length > 0 && featureDataDefault?.length > 0 && !commonLoading) {
            const overallDifference =
                Number(featureData[1].overall?.[0]?.change?.[1]) - Number(featureData[1].initial?.[0]?.change?.[1]);
            const overallDifferenceDefault =
                Number(featureDataDefault[1].overall?.[0]?.change?.[1]) -
                Number(featureDataDefault[1].initial?.[0]?.change?.[1]);
            const result = _.cloneDeep(featureData);
            result[3] = _.cloneDeep(featureDataDefault[0]);

            result[0].scale = timeSelectionLabel;
            result[0].overall = [featureData[0].overall, overallDifference];
            result[3].scale = defaultTimePeriodObject?.timeSelectionLabel;
            result[3].overall = [featureDataDefault[0].overall, overallDifferenceDefault];
            const subtractedResult = subtractObjects(_.cloneDeep(result[0]), _.cloneDeep(result[3]));
            result[4] = subtractedResult;
            result[4].scale = 'Delta';
            return result;
        }
        return featureData;
    }, [featureData, featureDataDefault, commonLoading]);

    const profitPoolData1 = profitPoolData?.data1;
    const profitPoolData2 = profitPoolData?.data2;
    const profitPoolExtra1 = profitPoolExtra?.extraData1;
    const profitPoolExtra2 = profitPoolExtra?.extraData2;

    const attributionExcelData = useMemo(() => {
        return salesAttributionChartDownload(extra?.headerTitle, commonData, volTile?.label);
    }, [commonData, extra?.headerTitle]);

    return (
        <div ref={salesAttributionChartRef}>
            {/* show attribution chart for volume,value and units */}
            {!isProfit ? (
                <div className="flex justify-between items-center gap-2 px-4 mb-4">
                    <p className={`${subHeaderClass}`}>
                        {attributionChartHeader} {expandedItem?.attributeExId}
                    </p>
                    <div className="flex items-center justify-end gap-2">
                        <CopyToClipBoard
                            removeScrollbar
                            elementClassToRemoveScrollbar="ant-table-content"
                            refOfComponent={salesAttributionChartRef}
                        />
                        <DownloadExcel
                            title={'AttributionChart'}
                            excelData={attributionExcelData.data}
                            headers={attributionExcelData.headers}
                        />
                    </div>
                    {showTileButtons && (
                        <TileButton
                            data={buttonList}
                            activeKey={attributeTile.key}
                            onClick={(item: any) => setAttributeTile(item)}
                        />
                    )}
                </div>
            ) : null}

            {/* sales attribution chart */}
            {/* show attribution chart for volume,value and units */}
            {!isProfit ? (
                <Table
                    ref={salesAttributionTableRef}
                    id="saleAttributionTable"
                    className={tableStyle}
                    pagination={false}
                    loading={commonLoading || loading}
                    locale={{
                        emptyText: commonError ? <ErrorBox msg={String(commonError)} /> : '',
                    }}
                    columns={getSalesAttributionColumns(
                        [salesAttributionChartConfig?.colorList, salesAttributionChartConfig?.profitPoolColor],
                        volTile,
                        commonData,
                        'attributionChart',
                        {
                            max: extra?.maxValue,
                            min: extra?.minValue,
                            headerTitle: extra?.headerTitle,

                            barLength: extra?.length,
                            onClick: (item: any) => {
                                trackEventMixpanel(GROWTH_ANALYSIS_SALES_CHART_FACTOR_CLICKED, {
                                    factorName: _.startCase(item),
                                });
                                if (onClick) onClick(item);
                            },
                            selected: selected,
                            width: containerWidth,
                            attributeTypeIndex: index,
                            config: appConfig,
                        },
                    )}
                    dataSource={commonData}
                    size="small"
                    scroll={{ x: 1000 }}
                    bordered={true}
                />
            ) : (
                // profit pool chart
                <div>
                    {/* RETAILER PROFIT POOL */}
                    <p className={`${subHeaderClass} pl-4 mb-4`}>{profitPoolRetailerHeader}</p>
                    <Table
                        ref={salesAttributionTableRef}
                        id="profitPoolRetailer"
                        className={tableStyle}
                        pagination={false}
                        loading={profitPoolDataLoading || loading}
                        locale={{
                            emptyText: profitPoolError ? <ErrorBox msg={String(profitPoolError)} /> : '',
                        }}
                        columns={getSalesAttributionColumns(
                            [salesAttributionChartConfig?.colorList, salesAttributionChartConfig?.profitPoolColor],
                            volTile,
                            profitPoolData1,
                            'profitPoolRetailer',
                            {
                                max: profitPoolExtra1?.maxValue,
                                min: profitPoolExtra1?.minValue,
                                headerTitle: profitPoolExtra1?.headerTitle,

                                barLength: profitPoolExtra1?.length,
                                onClick: undefined,
                                selected: selected,
                                width: containerWidth,
                                attributeTypeIndex: index,
                            },
                        )}
                        dataSource={profitPoolData1}
                        size="small"
                        scroll={{ x: 1000 }}
                        bordered={true}
                    />
                    {/* OWN PROFIT POOL */}
                    <p className={`${subHeaderClass} pl-4 my-4`}>{profitPoolOwnHeader}</p>
                    <Table
                        ref={salesAttributionTableRef}
                        id="profitPoolOwn"
                        className={tableStyle}
                        pagination={false}
                        loading={profitPoolDataLoading || loading}
                        locale={{
                            emptyText: profitPoolError ? <ErrorBox msg={String(profitPoolError)} /> : '',
                        }}
                        columns={getSalesAttributionColumns(
                            [salesAttributionChartConfig?.colorList, salesAttributionChartConfig?.profitPoolColor],
                            volTile,
                            profitPoolData2,
                            'profitPoolOwn',
                            {
                                max: profitPoolExtra2?.maxValue,
                                min: profitPoolExtra2?.minValue,
                                headerTitle: profitPoolExtra2?.headerTitle,

                                barLength: profitPoolExtra2?.length,
                                onClick: undefined,
                                selected: selected,
                                width: containerWidth,
                                attributeTypeIndex: index,
                            },
                        )}
                        dataSource={profitPoolData2}
                        size="small"
                        scroll={{ x: 1000 }}
                        bordered={true}
                    />
                </div>
            )}
        </div>
    );
};

export default SalesAttributionChart;

function subtractObjects(obj1: any, obj2: any) {
    const result: any = {};
    const toExcludeKeys = ['initial'];
    for (const key in obj1) {
        const val1 = obj1[key];
        const val2 = obj2[key];
        if (toExcludeKeys.includes(key)) {
            result[key] = null;
        }
        // Handle arrays - loop through and compare items
        else if (Array.isArray(val1) && Array.isArray(val2)) {
            // Both are arrays, compare items element-wise
            const maxLength = Math.max(val1.length, val2.length);
            result[key] = [];
            for (let i = 0; i < maxLength; i++) {
                const item1 = val1[i];
                const item2 = val2[i];

                // If items are numbers, subtract
                if (!_.isNil(item1) && !_.isNil(item2) && !isNaN(item1) && !isNaN(item2)) {
                    result[key][i] = item1 - item2;
                } else {
                    result[key][i] = null;
                }
            }
        }
        // Only subtract when both values are numbers
        else if (val1 !== null && val2 !== null && !isNaN(val1) && !isNaN(val2)) {
            result[key] = val1 - val2;
        } else {
            result[key] = null; // keep null when either value is missing
        }
    }
    return result;
}
